import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { getContrastTextColor } from "src/utils/color";
import { valueToLabel } from "../common";
import { SearchContext } from "src/search/useSearchContext";
import styles from "./Tooltip.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";
import { TooltipContentModule, TooltipContext } from "./tooltip-types";

interface HeatMapCellData {
  x: string;
  y: string;
  z: string;
  xField?: { target: string; attribute: string };
  yField?: { target: string; attribute: string };
  xType: { type: string; unit?: string };
  yType: { type: string; unit?: string };
  zType: { type: string; unit?: string };
  measureValue?: { label: string; value: string };
}

// Tooltip content module following the pattern of other tooltips
export const HeatMapTooltipModule: TooltipContentModule = {
  type: "group",
  priority: 1,
  shouldRender: (context: TooltipContext) => {
    return context.type === "group";
  },
  render: (context: TooltipContext) => {
    if (!context.record) return null;
    const aggregation = context.aggregations?.[0];

    return (
      <div className={styles.tooltip_container}>
        <div
          className={styles.tooltip_header}
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "var(--color-bg-container)",
            zIndex: 1,
          }}
        >
          Cell Information
        </div>
        <div>
          <div className={styles.tooltip_section}>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>X-Axis</div>
              <span className={styles.tooltip_value}>{context.record.x}</span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Y-Axis</div>
              <span className={styles.tooltip_value}>{context.record.y}</span>
            </div>
            {aggregation && (
              <>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Measure</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.field}
                  </span>
                </div>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Operator</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.reducer}
                  </span>
                </div>
              </>
            )}
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Value</div>
              <span className={styles.tooltip_value}>{context.record.z}</span>
            </div>
          </div>

          {context.groupBys && context.groupBys.length > 0 && (
            <>
              <div className={styles.tooltip_divider}></div>
              <div className={styles.tooltip_label}>Group By</div>
              {context.groupBys.map((group, index) => (
                <div key={index} className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>{group.target}:</div>
                  <span className={styles.tooltip_value}>
                    {group.attribute}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  },
};

export function HeatMapCellTooltip({
  cell,
  context,
  children,
}: {
  cell: HeatMapCellData;
  context: SearchContext;
  children: React.ReactNode;
}) {
  const { params } = context;
  const groupBys = params.state.groupBys || [];
  const aggregation = params.state.aggregations?.[0];

  const tooltipContext: TooltipContext = {
    type: "group",
    value: cell.z,
    formattedValue: cell.measureValue?.label || cell.z,
    record: cell,
    currentColumn: aggregation,
    metadata: {
      level: 0,
      path: [],
      isLeaf: true,
      depth: 0,
    },
    aggregations: [aggregation].filter(Boolean),
    groupBys,
    children: null,
  };

  return (
    <CustomTooltip
      content={HeatMapTooltipModule.render(tooltipContext)}
      placement="right"
      size="medium"
      overlayStyle={{ maxWidth: 400 }}
    >
      {children}
    </CustomTooltip>
  );
}
